import {httpClientV2} from "../../../../infrastructure/http";
import {DigitalProduct} from "../../domain/digital-product.entity";
import {
  CreateDigitalProductInput,
  CustomerPurchase,
  DateRange,
  DigitalProductSummary,
  ListParams,
  PaginatedResponse,
  ProductContent,
  SalesInsights,
  UpdateDigitalProductInput,
  ContentUploadOptions,
  AddOn,
  SaveAddOnsInput,
} from "../../domain/digital-product.types";
import {DigitalProductMapperV2} from "../mappers/digital-product.mapper.v2";
import {
  V2DigitalProductListResponse,
  V2GetProductResponse,
  V2GetContentResponse,
  V2SalesInsightsResponse,
  V2CustomerPurchasesResponse,
  V2GetAddOnsResponse,
} from "../mappers/digital-product.mapper.types";
import {DigitalProductRepository} from "./digital-product.repository";

/**
 * DigitalProductRepositoryV2
 *
 * Implementation using v2 API endpoints.
 * v2 endpoints use camelCase and `name` instead of `title`.
 */
export class DigitalProductRepositoryV2 implements DigitalProductRepository {
  async getList(params: ListParams): Promise<PaginatedResponse<DigitalProductSummary>> {
    const response = await httpClientV2.get<V2DigitalProductListResponse>(
      `digital-products?page=${params.page}&perPage=${params.perPage}`
    );
    return DigitalProductMapperV2.toSummaryList(response);
  }

  async getSingle(productId: number): Promise<DigitalProduct> {
    const response = await httpClientV2.get<V2GetProductResponse>(
      `digital-products/single/${productId}`
    );
    return DigitalProductMapperV2.toDomain(response);
  }

  async create(input: CreateDigitalProductInput): Promise<DigitalProduct> {
    const request = DigitalProductMapperV2.toCreateRequest(input);
    // v2 create returns the full created product (same shape as GET single).
    const response = await httpClientV2.post<V2GetProductResponse>(
      "digital-products",
      request
    );
    return DigitalProductMapperV2.toDomain(response);
  }

  async update(input: UpdateDigitalProductInput): Promise<DigitalProduct> {
    const request = DigitalProductMapperV2.toUpdateRequest(input);
    await httpClientV2.put("digital-products", request);
    return this.getSingle(input.productId);
  }

  async remove(productId: number): Promise<void> {
    await httpClientV2.delete(`digital-products/${productId}`);
  }

  async getContentList(productId: number): Promise<ProductContent[]> {
    const response = await httpClientV2.get<V2GetContentResponse>(
      `digital-products/${productId}/content`
    );
    return DigitalProductMapperV2.toContentList(response);
  }

  async uploadContent(
    productId: number,
    file: File,
    options?: ContentUploadOptions
  ): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);
    // Uploads append to the product's content list; callers refetch the list.
    await httpClientV2.post(`digital-products/${productId}/content`, formData, {
      onUploadProgress: options?.onProgress
        ? (event) => {
            if (event.total) {
              options.onProgress!(Math.round((event.loaded * 100) / event.total));
            }
          }
        : undefined,
    });
  }

  async deleteContent(productId: number, contentId: number): Promise<void> {
    await httpClientV2.delete(`digital-products/${productId}/content/${contentId}`);
  }

  async getSalesInsights(productId: number, range: DateRange): Promise<SalesInsights> {
    const response = await httpClientV2.get<V2SalesInsightsResponse>(
      `analytics/digital-products/sales-insights/${productId}?startDate=${range.startDate}&endDate=${range.endDate}`
    );
    return DigitalProductMapperV2.toSalesInsights(response);
  }

  async getCustomerPurchases(
    productId: number,
    params: ListParams,
    range: DateRange
  ): Promise<PaginatedResponse<CustomerPurchase>> {
    const response = await httpClientV2.get<V2CustomerPurchasesResponse>(
      `analytics/digital-products/customer-purchases/${productId}?page=${params.page}&perPage=${params.perPage}&startDate=${range.startDate}&endDate=${range.endDate}`
    );
    return DigitalProductMapperV2.toCustomerPurchases(response);
  }

  async exportCustomerPurchases(productId: number, range: DateRange): Promise<Blob> {
    return httpClientV2.get<Blob>(
      `analytics/digital-products/customer-purchases/export/${productId}?startDate=${range.startDate}&endDate=${range.endDate}`,
      {responseType: "blob"}
    );
  }

  async getAddOns(productId: number): Promise<AddOn[]> {
    const response = await httpClientV2.get<V2GetAddOnsResponse>(
      `digital-products/${productId}/add-ons`
    );
    return DigitalProductMapperV2.toAddOnList(response);
  }

  async createAddOns(productId: number, input: SaveAddOnsInput): Promise<AddOn[]> {
    const request = DigitalProductMapperV2.toSaveAddOnsRequest(input);
    const response = await httpClientV2.post<V2GetAddOnsResponse>(
      `digital-products/${productId}/add-ons`,
      request
    );
    return DigitalProductMapperV2.toAddOnList(response);
  }

  async updateAddOns(productId: number, input: SaveAddOnsInput): Promise<AddOn[]> {
    const request = DigitalProductMapperV2.toSaveAddOnsRequest(input);
    const response = await httpClientV2.put<V2GetAddOnsResponse>(
      `digital-products/${productId}/add-ons`,
      request
    );
    return DigitalProductMapperV2.toAddOnList(response);
  }

  async deleteAddOn(productId: number, addOnId: string): Promise<void> {
    await httpClientV2.delete(`digital-products/${productId}/add-ons/${addOnId}`);
  }

  async deleteAddOnOption(
    productId: number,
    addOnId: string,
    optionId: string
  ): Promise<void> {
    await httpClientV2.delete(
      `digital-products/${productId}/add-ons/${addOnId}/options/${optionId}`
    );
  }
}
